import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import './saved.css';
import './profile.css';
import {
  BottomNav,
  BUYER_NAV,
  ChevronRightIcon,
  ColorRail,
  CoinIcon,
  HeartOutlineIcon,
  MessageIcon,
  SELLER_NAV,
  TopBar,
} from '../../../commodity/components/caria';
import { useAuthChrome } from '../../../core/identity-access/hooks/useAuthChrome';
import { RootState } from '../../../store/store';

/**
 * Profile — the account surface, on the CAR-IA chrome with the prototype's
 * account-card + row-list language (docs/html-prototypes/CAR-IA/car-ia-cuenta.html).
 *
 * Replaces an inline placeholder in App.tsx ("Mi Perfil / Administra tu
 * información") that rendered inside the pre-lime forest-green MainLayout.
 * It shows what the app actually knows today — name, email, role — and links
 * to the surfaces that exist. Editing a profile is not built; the prototype's
 * settings/payment rows are deliberately not faked here.
 */

const ROLE_LABEL: Record<string, string> = {
  BUYER: 'Comprador',
  SELLER_PRIVATE: 'Vendedor particular',
  SELLER_DEALER: 'Dealer',
  ADMIN: 'Administrador',
  MODERATOR: 'Moderador',
};

export const Profile: React.FC = () => {
  const navigate = useNavigate();
  const authChrome = useAuthChrome();
  const user = useSelector((s: RootState) => s.auth.user);

  const isSeller = (user?.role ?? '').startsWith('SELLER');
  const initials =
    [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('').toUpperCase() ||
    (user?.email?.[0] ?? '?').toUpperCase();
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email;

  const links: { label: string; hint: string; to: string; Icon: React.FC<any> }[] = isSeller
    ? [
        {
          label: 'Mis anuncios',
          hint: 'Tus vehículos publicados',
          to: '/my-listings',
          Icon: CoinIcon,
        },
        {
          label: 'Publicar vehículo',
          hint: 'Crear un anuncio nuevo',
          to: '/publish',
          Icon: CoinIcon,
        },
      ]
    : [
        {
          label: 'Guardados',
          hint: 'Vehículos que marcaste',
          to: '/saved',
          Icon: HeartOutlineIcon,
        },
        {
          label: 'Mis contactos',
          hint: 'Vendedores con los que hablaste',
          to: '/my-contacts',
          Icon: MessageIcon,
        },
      ];

  return (
    <div className="cia-saved">
      <TopBar {...authChrome} nav={isSeller ? SELLER_NAV : BUYER_NAV} />
      <ColorRail />

      <div className="cia-saved-wrap">
        <div className="cia-saved-head">
          <h1>Mi perfil</h1>
        </div>
        <p className="cia-saved-sub">Tu cuenta en CAR-IA.</p>

        <div className="cia-prof-card" data-testid="profile-card">
          <span className="cia-prof-avatar">{initials}</span>
          <span className="cia-prof-id">
            <b>{fullName ?? 'Tu cuenta'}</b>
            <small>{user?.email}</small>
            {user?.role && (
              <span className="cia-prof-role">{ROLE_LABEL[user.role] ?? user.role}</span>
            )}
          </span>
        </div>

        <div className="cia-saved-list" style={{ marginTop: 14 }}>
          {links.map(({ label, hint, to, Icon }) => (
            <button key={to} className="cia-saved-row cia-prof-row" onClick={() => navigate(to)}>
              <span className="cia-prof-ic">
                <Icon size={18} strokeWidth={1.8} />
              </span>
              <span className="cia-saved-info">
                <span className="cia-saved-title">{label}</span>
                <span className="cia-saved-meta">{hint}</span>
              </span>
              <ChevronRightIcon size={16} />
            </button>
          ))}
        </div>

        <p className="cia-prof-note">
          Editar tu información, métodos de pago y preferencias llega en un incremento posterior.
        </p>
      </div>

      <BottomNav />
    </div>
  );
};

export default Profile;
